/**
 * The web theme runtime: read/persist the user's CHOICE, resolve it to a
 * concrete theme, and apply it to <html>.
 *
 * We store the choice (light | dark | system), never the resolved color, so
 * "Match system" keeps tracking the OS live. The server is the source of truth
 * (synced at login); localStorage is the fast-path cache the boot script reads
 * before first paint so there is no flash of the wrong theme.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'tc-theme';

// Phase 5: the picker is live but light mode is opt-in — the default stays
// 'dark' so nobody is auto-flipped while the remaining hardcoded colors are
// validated. Change to 'system' to honor the OS once light-mode QA is signed off.
export const DEFAULT_CHOICE: ThemeChoice = 'dark';

// Phase 5: the user-facing theme picker is live (Profile → Appearance).
export const THEME_PICKER_ENABLED = true;

// The browser-chrome color for <meta name="theme-color">. It must be a concrete
// value — the meta tag cannot resolve a CSS variable — and is the ground color
// of each theme, kept in step with colors.ts/colors.light.ts.
// eslint-disable-next-line no-restricted-syntax -- meta theme-color needs a literal, not a var()
const META_COLORS: Record<ResolvedTheme, string> = { dark: '#000000', light: '#FAF9F7' };

export function isThemeChoice(v: unknown): v is ThemeChoice {
  return v === 'light' || v === 'dark' || v === 'system';
}

export function readStoredChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeChoice(v)) return v;
  } catch {
    /* private mode / blocked storage — fall through to default */
  }
  return DEFAULT_CHOICE;
}

export function writeStoredChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* not remembered; the session still holds it in React state */
  }
}

export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveChoice(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : choice;
}

/** Apply a resolved theme to the document: the attribute the CSS keys on, plus
 *  the browser-chrome color so mobile address bars match. */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_COLORS[resolved]);
}
